import type { ConditionEvaluator } from "../../types/conditionEvaluators.ts";
import type {
  IObjectArrayBinaryCondition,
  IObjectArrayTernaryCondition,
  ObjectArrayCondition,
} from "../../types/conditions/arrays/objects.ts";
import { Operator } from "../../types/operators.ts";
import type { ValueResolver } from "../../types/resolvers.ts";
import type {
  IObjectPropertyValue,
  IResolvableObjectArrayValue,
  Obj,
  ResolvableBooleanValue,
  ResolvableValue,
} from "../../types/values.ts";
import { isResolvable } from "../../valueResolvers/shared/index.ts";
import { createThrowConditionNotImplementedForEvaluator, getArrayValues } from "../common.ts";

const getIndices = (
  subject: Obj[] | IResolvableObjectArrayValue,
  resolve: ValueResolver,
  condition: ResolvableBooleanValue,
) => {
  const indices: number[] = [];

  getArrayValues("object", subject, resolve).forEach((object, index) => {
    if (resolve(applyObjectProperty(condition, object), resolve)) {
      indices.push(index);
    }
  });

  return indices;
};

// patch in the "object" of "objectProperty"
// this is a bit clumsy, better to inject a resolver on the fly or add a context or something
// but getting it working first
const applyObjectProperty = (condition: ResolvableValue, object: Obj) => {
  const setObject = (element: ResolvableValue) => {
    if (element && isResolvable(element) && element.resolveType === "objectProperty") {
      const typed = element as IObjectPropertyValue;
      typed.object = object;
    } else if (element && typeof element === "object") {
      for (const child of Object.values(element)) {
        if (Array.isArray(child)) {
          for (const gChild of child) {
            setObject(gChild as ResolvableValue);
          }
        } else if (typeof child === "object") {
          setObject(child as ResolvableValue);
        }
      }
    }
  };
  setObject(condition);
  return condition;
};

const throwNotImplemented = createThrowConditionNotImplementedForEvaluator("object[]");

const binaryConditionEvaluator: ConditionEvaluator<IObjectArrayBinaryCondition> = (
  { subject, condition, operator, type },
  resolve,
): boolean => {
  const subjectValues = getArrayValues("object", subject, resolve);

  const matchesCondition = (value: Obj) => resolve(applyObjectProperty(condition, value), resolve);

  switch (operator) {
    case Operator.Has:
      return !!subjectValues.find(matchesCondition);

    case Operator.NotHas:
      return !subjectValues.find(matchesCondition);

    case Operator.First:
      return subjectValues.findIndex(matchesCondition) === 0;

    case Operator.Last:
      return subjectValues.findIndex(matchesCondition) === subjectValues.length - 1;

    default:
      throw new Error(`Condition type for ${type} not implemented for Obj[]`);
  }
};

const ternaryConditionEvaluator: ConditionEvaluator<IObjectArrayTernaryCondition> = (
  { subject, operator, leftCondition, rightCondition },
  resolve,
): boolean => {
  const leftIndices = getIndices(subject, resolve, leftCondition);
  const rightIndices = getIndices(subject, resolve, rightCondition);

  if (leftIndices.length === 0 || rightIndices.length === 0) return false;

  switch (operator) {
    case Operator.Before: {
      const minimumIndex = Math.min(...leftIndices);
      const maximumIndex = Math.max(...rightIndices);

      return minimumIndex < maximumIndex;
    }

    case Operator.After: {
      const maximumIndex = Math.max(...leftIndices);
      const minimumIndex = Math.min(...rightIndices);

      return maximumIndex > minimumIndex;
    }

    default:
      throw new Error(`Condition operator ${operator} not implemented for Obj[]`);
  }
};

export const conditionEvaluator: ConditionEvaluator<ObjectArrayCondition> = (condition, resolve): boolean => {
  switch (condition.operator) {
    case Operator.Has:
    case Operator.NotHas:
    case Operator.First:
    case Operator.Last:
    case Operator.Contains:
    case Operator.NotContains:
      return binaryConditionEvaluator(condition, resolve);

    case Operator.Before:
    case Operator.After:
      return ternaryConditionEvaluator(condition, resolve);

    default:
      return throwNotImplemented(condition);
  }
};

export default conditionEvaluator;
